import React, { useState, useMemo } from 'react';
import ReinnoireCard from '../Shared/ReinnoireCard';
import DateField from '../Shared/DateField';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import './Tracker.css';

/* ── Tarife OSIM design (LEI, Anexa nr.5 la O.G. 41/1998, valabile 01.01.2026) ── */
const TARIFE_OSIM_DEFAULT = {
  depunere_primul:      152,  // pct. 1 — cerere
  depunere_suplimentar:  51,  // pct. 1 — fiecare design suplimentar
  publicare_figura_an:  102,  // pct. 2 — figura 6x6cm, alb-negru
  publicare_figura_color:508, // pct. 2 — figura 6x6cm, color
  publicare_text:        51,  // pct. 2 — fiecare 30 cuvinte
  amanare_publicare:    102,  // pct. 3
  prioritate:           102,  // pct. 4
  examinare_primul:     254,  // pct. 5
  examinare_suplimentar: 51,  // pct. 5 — suplimentar
  certificat_1_20:      102,  // pct. 6
  certificat_21_50:     152,
  certificat_51_100:    254,
  mentinere_1_20:       508,  // pct. 7 — 5 ani
  mentinere_21_50:      635,
  mentinere_51_100:     762,
  cert_reinnoire_1_20:  102,  // pct. 8
  cert_reinnoire_21_50: 127,
  cert_reinnoire_51_100:152,
  reinnoire_1_20:       508,  // pct. 9 — 5 ani
  reinnoire_21_50:      635,
  reinnoire_51_100:     762,
  haga_primul:          406,  // pct. 18
  haga_urmatoarele:     102,
  prelungire_termen:     51,
};

/* ── Taxe EUIPO / RCD default (EUR, Regulamentul (UE) 2024/2822, aplicabil 01.05.2025) ── */
const TAXE_EUIPO_DEFAULT = {
  depunere_primul:       350,  // D-101
  depunere_suplimentar:  125,  // D-102
  amanare_primul:         40,  // D-021
  amanare_suplimentar:    20,  // D-022
  reinnoire_1:           150,  // D-041, pe desen — 6–10 ani
  reinnoire_2:           250,  // D-042 — 11–15 ani
  reinnoire_3:           400,  // D-043 — 16–20 ani
  reinnoire_4:           700,  // D-044 — 21–25 ani
  haga_desemnare:         62,  // taxa individuala de desemnare a UE, pe desen
  haga_reinnoire:         62,  // taxa individuala de reinnoire, pe desen
};

/* ── Perioadele de protectie OSIM (max 25 ani, mentinere + reinnoire) ── */
const PERIOADE_OSIM = [
  { nr:1, ani:'1–5',   start:0,  tip:'mentinere' },
  { nr:2, ani:'6–10',  start:5,  tip:'mentinere' },
  { nr:3, ani:'11–15', start:10, tip:'reinnoire'  },
  { nr:4, ani:'16–20', start:15, tip:'reinnoire'  },
  { nr:5, ani:'21–25', start:20, tip:'reinnoire'  },
];

/* ── Perioadele de reinnoire WIPO Hague (max 25 ani) ── */
const PERIOADE_WIPO = [
  { nr:1, ani:'6–10',  start:5  },
  { nr:2, ani:'11–15', start:10 },
  { nr:3, ani:'16–20', start:15 },
  { nr:4, ani:'21–25', start:20 },
];

/* ── Perioadele de reinnoire EUIPO (4 reinnoire, 5 ani fiecare) ── */
const PERIOADE_EUIPO = [
  { nr:1, ani:'6–10',  start:5,  cheie:'reinnoire_1' },
  { nr:2, ani:'11–15', start:10, cheie:'reinnoire_2' },
  { nr:3, ani:'16–20', start:15, cheie:'reinnoire_3' },
  { nr:4, ani:'21–25', start:20, cheie:'reinnoire_4' },
];

/* ── Helpers ── */
const interval = (n) => (n <= 20 ? '1_20' : n <= 50 ? '21_50' : '51_100');
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addYears  = (d, y) => { const x = new Date(d); x.setFullYear(x.getFullYear() + y); return x; };
const addDays   = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtLei = (n) => Number(n).toLocaleString('ro-RO') + ' lei';
const fmtEur = (n) => Number(n).toLocaleString('ro-RO') + ' €';

/* ── emptyRecord export ── */
export function emptyRecord() {
  return {
    denumire: '',
    oficiu: 'osim',
    solicitant: '',
    autori: '',
    clasaLocarno: '',
    produseAplicare: '',
    nrDesignuri: 1,
    figuriAN: 1,        // figuri alb-negru pentru publicare (OSIM)
    figuriColor: 0,     // figuri color pentru publicare (OSIM)
    blocuriText: 1,     // blocuri de 30 de cuvinte (OSIM)
    tipPublicare: 'color',
    amanarePublicare: false,
    dataAmanare: '',
    reducere50: false,  // reducere 50% OSIM (art. 26 O.G. 41/1998)
    haga: false,        // cale Hague System (WIPO / desemnare UE la EUIPO)
    hagaModele: 1,
    depusa: false,
    numar: '',
    dataDepunere: '',
    dataExaminare: '',
    dataPublicare: '',
    dataInregistrare: '',
    dataExpirare: '',
    certificatPrimit: false,
    transmisClient: false,
    perioade: {},       // { "1": true, "2": true, ... } — perioade OSIM platite
    reinnoiri: {},      // { "1": true, ... } — reinnoiri EUIPO platite
    reinnoiriWipo: {},  // { "1": true, ... } — reinnoiri WIPO Hague platite
    // WIPO Hague specific
    tariDesemnate: '',
    dataPublicareWIPO: '',
    taxeWipo: '',
    observatii: '',
  };
}

/* ── Fee calculation ── */
function calcTaxeOsim(r, T) {
  const p = r.reducere50 ? 0.5 : 1;
  const red = (v) => Math.round(v * p);
  const n = Math.max(1, Number(r.nrDesignuri) || 1);
  const iv = interval(n);

  const depunere = red(T.depunere_primul + (n - 1) * T.depunere_suplimentar);
  const pubAN = Number(r.figuriAN || 0) * T.publicare_figura_an;
  const pubColor = Number(r.figuriColor || 0) * T.publicare_figura_color;
  const pubText = Number(r.blocuriText || 0) * T.publicare_text;
  const publicare = red(pubAN + pubColor + pubText);
  const examinare = red(T.examinare_primul + (n - 1) * T.examinare_suplimentar);
  const certificat = red(T[`certificat_${iv}`] || 0);
  const mentinere1 = red(T[`mentinere_${iv}`] || 0);
  // nereductibile (art. 26 alin. 2)
  const amanare = r.amanarePublicare ? T.amanare_publicare : 0;
  const prioritate = 0; // se adauga manual
  const haga = r.haga ? (T.haga_primul + Math.max(0, Number(r.hagaModele || 1) - 1) * T.haga_urmatoarele) : 0;
  const panaCertificat = depunere + publicare + amanare + examinare + certificat + mentinere1;
  return { n, iv, p, depunere, publicare, pubAN, pubColor, pubText, amanare, prioritate, examinare, certificat, mentinere1, haga, panaCertificat };
}

function calcTaxeEuipo(r, T) {
  const n = Math.max(1, Math.min(50, Number(r.nrDesignuri) || 1));
  const depunere = r.haga
    ? n * T.haga_desemnare
    : T.depunere_primul + (n - 1) * T.depunere_suplimentar;
  const amanare = r.amanarePublicare && !r.haga
    ? T.amanare_primul + (n - 1) * T.amanare_suplimentar
    : 0;
  const total = depunere + amanare;
  return { n, depunere, amanare, total };
}

/* ── Scadentar perioade OSIM ── */
function calcPerioadeOsim(r, T) {
  const dep = parseISO(r.dataDepunere || r.dataPublicare);
  const hot = parseISO(r.dataExaminare); // folosim data examinarii ca proxy pt hotarare
  if (!dep) return [];
  const iv = interval(Math.max(1, Number(r.nrDesignuri) || 1));
  const p = r.reducere50 ? 0.5 : 1;
  return PERIOADE_OSIM.map(per => {
    const start = new Date(dep); start.setFullYear(start.getFullYear() + per.start);
    const sfarsit = new Date(dep); sfarsit.setFullYear(sfarsit.getFullYear() + per.start + 5);
    const suma = per.tip === 'mentinere'
      ? Math.round(T[`mentinere_${iv}`] * p)
      : T[`reinnoire_${iv}`]; // reinnoire nu beneficiaza de reducere
    const certReinnoire = per.tip === 'reinnoire' ? T[`cert_reinnoire_${iv}`] : 0;
    const scadenta = per.nr === 1 && hot
      ? new Date(hot.getTime() + 30 * 24 * 3600 * 1000) // o luna de la hotarare
      : start;
    const gratie = new Date(scadenta.getTime() + 6 * 30 * 24 * 3600 * 1000);
    const platit = !!(r.perioade || {})[per.nr];
    return { ...per, start, sfarsit, scadenta, gratie, suma, certReinnoire, platit };
  });
}

/* ── Scadentar reinnoiri EUIPO ── */
function calcReinnoiriEuipo(r, T) {
  const dep = parseISO(r.dataDepunere);
  if (!dep) return [];
  const n = Math.max(1, Math.min(50, Number(r.nrDesignuri) || 1));
  return PERIOADE_EUIPO.map(per => {
    const expira = new Date(dep); expira.setFullYear(expira.getFullYear() + per.start);
    const fereastraStart = new Date(expira.getTime() - 6 * 30 * 24 * 3600 * 1000);
    const gratie = new Date(expira.getTime() + 6 * 30 * 24 * 3600 * 1000);
    const unitar = r.haga ? T.haga_reinnoire : T[per.cheie];
    const suma = unitar * n;
    const platit = !!(r.reinnoiri || {})[per.nr];
    return { ...per, expira, fereastraStart, gratie, unitar, suma, platit };
  });
}

/* ── Status / timeline (per office) ── */
function deriveStatusOsim(r) {
  const expirare = parseISO(r.dataPublicare) ? addYears(parseISO(r.dataPublicare), 5) : null;
  const registered = !!(r.dataInregistrare);

  const steps = [
    { key:'depunere',   label:'Depunere',            done: registered || !!r.depusa,          date: parseISO(r.dataDepunere) },
    { key:'examinare',  label:'Examinare formala',   done: registered || !!r.dataExaminare,   date: parseISO(r.dataExaminare) },
    { key:'publicare',  label:'Publicare BOPI',      done: registered || !!r.dataPublicare,   date: parseISO(r.dataPublicare) },
    { key:'certificat', label:'Certificat primit',   done: r.certificatPrimit,   date: null },
    { key:'client',     label:'Transmis client',     done: r.transmisClient,     date: null },
  ];

  let alert;
  if (registered) alert = (r.certificatPrimit && !r.transmisClient)
    ? { label:'De transmis certificatul catre client', sev:'act' }
    : { label:'Design inregistrat', sev:'done' };
  else if (!r.depusa) alert = { label:'In pregatire — de depus', sev:'neutral' };
  else if (!r.dataExaminare) alert = { label:'Asteptare examinare formala', sev:'info' };
  else if (!r.dataPublicare) alert = { label:'Asteptare publicare BOPI', sev:'info' };
  else if (!r.certificatPrimit) alert = { label:'De obtinut certificatul de inregistrare', sev:'act' };
  else if (!r.transmisClient) alert = { label:'De transmis certificatul catre client', sev:'act' };
  else alert = { label:'Procedura finalizata', sev:'done' };

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  if (expirare && (alert.sev === 'done' || alert.sev === 'act')) {
    const dl = daysLeft(expirare);
    if (dl <= 365) {
      alert = { label: dl < 0 ? 'Design expirat — reinnoire necesara' : 'Design expira — reinnoire necesara', date: expirare, daysLeft: dl, sev: dl < 0 ? 'overdue' : 'watch' };
    }
  }

  return { steps, alert, expirare };
}

function deriveStatusEuipo(r) {
  const expirare = parseISO(r.dataInregistrare) ? addYears(parseISO(r.dataInregistrare), 5) : null;
  const registered = !!(r.dataInregistrare);

  const steps = [
    { key:'depunere',   label:'Depunere',                    done: registered || !!r.depusa,                             date: parseISO(r.dataDepunere) },
    { key:'examinare',  label:'Examinare formala',            done: registered || !!r.dataExaminare,                     date: parseISO(r.dataExaminare) },
    { key:'publicare',  label:'Publicare (sau amanare)',      done: registered || !!r.dataPublicare || r.amanarePublicare, date: parseISO(r.dataPublicare) },
    { key:'inreg',      label:'Inregistrare',                 done: registered || !!r.dataInregistrare,                  date: parseISO(r.dataInregistrare) },
    { key:'certificat', label:'Certificat primit',            done: r.certificatPrimit,                   date: null },
    { key:'client',     label:'Transmis client',              done: r.transmisClient,                     date: null },
  ];

  let alert;
  if (registered) alert = (r.certificatPrimit && !r.transmisClient)
    ? { label:'De transmis certificatul catre client', sev:'act' }
    : { label:'Design inregistrat', sev:'done' };
  else if (!r.depusa) alert = { label:'In pregatire — de depus', sev:'neutral' };
  else if (!r.dataExaminare) alert = { label:'Asteptare examinare formala', sev:'info' };
  else if (!r.dataPublicare && !r.amanarePublicare) alert = { label:'Asteptare publicare', sev:'info' };
  else if (r.amanarePublicare && r.dataAmanare) alert = { label:'Publicare amanata', date: parseISO(r.dataAmanare), sev:'watch' };
  else if (!r.dataInregistrare) alert = { label:'Asteptare inregistrare', sev:'info' };
  else if (!r.certificatPrimit) alert = { label:'De obtinut certificatul de inregistrare', sev:'act' };
  else if (!r.transmisClient) alert = { label:'De transmis certificatul catre client', sev:'act' };
  else alert = { label:'Procedura finalizata', sev:'done' };

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  if (expirare && (alert.sev === 'done' || alert.sev === 'act')) {
    const dl = daysLeft(expirare);
    if (dl <= 365) {
      alert = { label: dl < 0 ? 'Design expirat — reinnoire necesara' : 'Design expira — reinnoire necesara', date: expirare, daysLeft: dl, sev: dl < 0 ? 'overdue' : 'watch' };
    }
  }

  return { steps, alert, expirare };
}

function deriveStatusWipo(r) {
  const expirare = parseISO(r.dataInregistrare) ? addYears(parseISO(r.dataInregistrare), 5) : null;
  const registered = !!(r.dataInregistrare);

  const steps = [
    { key:'depunere',   label:'Depunere Hague',           done: registered || !!r.depusa,               date: parseISO(r.dataDepunere) },
    { key:'publicare',  label:'Publicare Buletin WIPO',   done: registered || !!r.dataPublicareWIPO,    date: parseISO(r.dataPublicareWIPO) },
    { key:'inreg',      label:'Inregistrare internationala', done: registered || !!r.dataInregistrare,  date: parseISO(r.dataInregistrare) },
    { key:'certificat', label:'Certificat primit',         done: r.certificatPrimit,         date: null },
    { key:'client',     label:'Transmis client',           done: r.transmisClient,           date: null },
  ];

  let alert;
  if (registered)              alert = (r.certificatPrimit && !r.transmisClient)
    ? { label:'De transmis certificatul catre client', sev:'act' }
    : { label:'Design inregistrat', sev:'done' };
  else if (!r.depusa)          alert = { label:'In pregatire — de depus', sev:'neutral' };
  else if (!r.dataPublicareWIPO) alert = { label:'Asteptare publicare in Buletinul WIPO', sev:'info' };
  else if (!r.dataInregistrare)  alert = { label:'Asteptare inregistrare internationala', sev:'info' };
  else if (!r.certificatPrimit)  alert = { label:'De obtinut certificatul international', sev:'act' };
  else if (!r.transmisClient)    alert = { label:'De transmis certificatul catre client', sev:'act' };
  else                           alert = { label:'Procedura finalizata', sev:'done' };

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  if (expirare && (alert.sev === 'done' || alert.sev === 'act')) {
    const dl = daysLeft(expirare);
    if (dl <= 365) {
      alert = { label: dl < 0 ? 'Inregistrare expirata — reinnoire necesara' : 'Inregistrare expira — reinnoire necesara', date: expirare, daysLeft: dl, sev: dl < 0 ? 'overdue' : 'watch' };
    }
  }

  return { steps, alert, expirare };
}

function calcReinnoiriWipo(r) {
  const dep = parseISO(r.dataDepunere);
  if (!dep) return [];
  return PERIOADE_WIPO.map(per => {
    const expira = new Date(dep); expira.setFullYear(expira.getFullYear() + per.start);
    const fereastraStart = new Date(expira.getTime() - 6 * 30 * 24 * 3600 * 1000);
    const gratie = new Date(expira.getTime() + 6 * 30 * 24 * 3600 * 1000);
    const platit = !!(r.reinnoiriWipo || {})[per.nr];
    return { ...per, expira, fereastraStart, gratie, platit };
  });
}

/* ════════════════════════ Component ════════════════════════ */
export default function DesignMITracker({ data, onChange, procId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);
  const [tarifeOsim, setTarifeOsim] = useState(TARIFE_OSIM_DEFAULT);
  const [taxeEuipo, setTaxeEuipo] = useState(TAXE_EUIPO_DEFAULT);
  const [showTarife, setShowTarife] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const isOsim = r.oficiu === 'osim';
  const isEuipo = r.oficiu === 'euipo';
  const isWipo = r.oficiu === 'wipo';

  const taxeOsim = useMemo(() => calcTaxeOsim(r, tarifeOsim), [r, tarifeOsim]);
  const taxeEuipoCalc = useMemo(() => calcTaxeEuipo(r, taxeEuipo), [r, taxeEuipo]);
  const status = useMemo(() => isEuipo ? deriveStatusEuipo(r) : isWipo ? deriveStatusWipo(r) : deriveStatusOsim(r), [r, isEuipo, isWipo]);
  const perioadeOsim = useMemo(() => isOsim ? calcPerioadeOsim(r, tarifeOsim) : [], [r, tarifeOsim, isOsim]);
  const reinnoiriEuipo = useMemo(() => isEuipo ? calcReinnoiriEuipo(r, taxeEuipo) : [], [r, taxeEuipo, isEuipo]);
  const reinnoiriWipo = useMemo(() => isWipo ? calcReinnoiriWipo(r) : [], [r, isWipo]);

  const togglePerioadaOsim = (nr) => {
    const p = { ...(r.perioade || {}), [nr]: !(r.perioade || {})[nr] };
    set({ perioade: p });
  };
  const toggleReinnoireEuipo = (nr) => {
    const p = { ...(r.reinnoiri || {}), [nr]: !(r.reinnoiri || {})[nr] };
    set({ reinnoiri: p });
  };
  const toggleReinnoireWipo = (nr) => {
    const p = { ...(r.reinnoiriWipo || {}), [nr]: !(r.reinnoiriWipo || {})[nr] };
    set({ reinnoiriWipo: p });
  };

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea design-ului">
        <div className="f-row">
          <div className="field">
            <label>Denumire design</label>
            <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: Ambalaj Aurora Style" />
          </div>
          <div className="field">
            <label>Oficiu</label>
            <SegControl value={r.oficiu} onChange={v => set({ oficiu: v })} options={[['osim','OSIM'],['euipo','EUIPO (RCD)'],['wipo','WIPO (Hague)']]} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Solicitant / Titular</label>
            <input type="text" value={r.solicitant} onChange={e => set({ solicitant: e.target.value })} />
          </div>
          <div className="field">
            <label>Autori / Designeri</label>
            <input type="text" value={r.autori} onChange={e => set({ autori: e.target.value })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Produsul la care se aplica</label>
            <input type="text" value={r.produseAplicare} onChange={e => set({ produseAplicare: e.target.value })} placeholder="ex: ambalaje, etichete" />
          </div>
          <div className="field">
            <label>Clasa Locarno</label>
            <input type="text" value={r.clasaLocarno} onChange={e => set({ clasaLocarno: e.target.value })} placeholder="ex: 09-01" className="mono" />
          </div>
        </div>
        <div className="field">
          <label>Reprezentari grafice <span className="f-hint">— vederi multiple ale design-ului</span></label>
          <ProceduraFileSection procId={procId} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange} />
        </div>
        <div className="f-row">
          <div className="field">
            <label>Nr. design-uri in cerere</label>
            <input type="number" min="1" max={isEuipo ? 50 : 100} value={r.nrDesignuri} onChange={e => set({ nrDesignuri: Number(e.target.value) || 1 })} />
          </div>
          {isOsim && (
            <>
              <div className="field">
                <label>Figuri alb-negru</label>
                <input type="number" min="0" value={r.figuriAN || 0} onChange={e => set({ figuriAN: Number(e.target.value) || 0 })} />
              </div>
              <div className="field">
                <label>Figuri color</label>
                <input type="number" min="0" value={r.figuriColor || 0} onChange={e => set({ figuriColor: Number(e.target.value) || 0 })} />
              </div>
              <div className="field">
                <label>Blocuri text (30 cuv.)</label>
                <input type="number" min="0" value={r.blocuriText || 0} onChange={e => set({ blocuriText: Number(e.target.value) || 0 })} />
              </div>
            </>
          )}
        </div>
        {isWipo && (
          <div className="field">
            <label>Tari desemnate</label>
            <input type="text" value={r.tariDesemnate} onChange={e => set({ tariDesemnate: e.target.value })} placeholder="ex: RO, DE, FR, IT" />
            <span className="f-hint">Tarile membre Actul 1999 in care se solicita protectie.</span>
          </div>
        )}
        {isOsim && (
          <label className="check-label">
            <input type="checkbox" checked={r.reducere50} onChange={e => set({ reducere50: e.target.checked })} />
            Reducere 50% (art. 26 O.G. 41/1998 — persoane fizice / IMM)
          </label>
        )}
        {!isWipo && (
          <>
            <label className="check-label">
              <input type="checkbox" checked={r.amanarePublicare} onChange={e => set({ amanarePublicare: e.target.checked })} />
              Amanarea publicarii ({isOsim ? 'max 6 luni OSIM' : 'max 30 luni EUIPO'})
            </label>
            {r.amanarePublicare && (
              <div className="field" style={{ marginLeft: 22 }}>
                <label>Data la care expira amanarea</label>
                <DateField value={r.dataAmanare} onChange={v => set({ dataAmanare: v })} />
              </div>
            )}
          </>
        )}
      </TrackerCard>

      {isWipo && (
        <TrackerCard title="Taxe WIPO (Hague)">
          <div className="field">
            <label>Valoare taxe oficiale</label>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="number" min="0" step="any" value={r.taxeWipo}
                onChange={e => set({ taxeWipo: e.target.value })} placeholder="ex: 397"
                style={{ maxWidth: 160 }} />
              <span style={{ fontWeight:600, color:'var(--muted)' }}>CHF</span>
            </div>
            <span className="f-hint">Folositi calculatorul oficial WIPO (Fee Calculator) pentru taxele Hague. Suma include taxele de baza si taxele individuale de desemnare.</span>
          </div>
        </TrackerCard>
      )}

      {isOsim && (
        <TrackerCard title="Calcul taxe OSIM" badge="tarife 01.01.2026, LEI">
          <table className="fee-table">
            <tbody>
              <FeeRow lei label={`Depunere (1 design + ${Math.max(0, taxeOsim.n - 1)} supl.)`} value={taxeOsim.depunere} />
              {taxeOsim.pubAN > 0 && <FeeRow lei label={`Publicare ${r.figuriAN} fig. a/n`} value={taxeOsim.pubAN} />}
              {taxeOsim.pubColor > 0 && <FeeRow lei label={`Publicare ${r.figuriColor} fig. color`} value={taxeOsim.pubColor} />}
              {taxeOsim.pubText > 0 && <FeeRow lei label={`Publicare ${r.blocuriText} blocuri text`} value={taxeOsim.pubText} />}
              {taxeOsim.publicare === 0 && <FeeRow lei label="Publicare BOPI" value={0} />}
              {taxeOsim.amanare > 0 && <FeeRow lei label="Taxa amanare publicare" value={taxeOsim.amanare} />}
              <FeeRow lei label={`Examinare (${taxeOsim.n} design${taxeOsim.n > 1 ? '-uri' : ''})`} value={taxeOsim.examinare} />
              <FeeRow lei label={`Eliberare certificat (lot ${taxeOsim.iv})`} value={taxeOsim.certificat} />
              <FeeRow lei label={`Mentinere per. I (lot ${taxeOsim.iv})`} value={taxeOsim.mentinere1} />
              {taxeOsim.haga > 0 && <FeeRow lei label="Desemnare OSIM prin Haga" value={taxeOsim.haga} />}
            </tbody>
            <tfoot>
              <tr className="fee-total">
                <td>Total pana la certificat{r.reducere50 ? ' (red. 50%)' : ''}</td>
                <td>{fmtLei(taxeOsim.panaCertificat)}</td>
              </tr>
            </tfoot>
          </table>
          <button type="button" className="btn-link mt8" onClick={() => setShowTarife(s => !s)}>
            {showTarife ? '▾' : '▸'} Tarife oficiale (editabile)
          </button>
          {showTarife && <TarifeEditorOsim tarife={tarifeOsim} setTarife={setTarifeOsim} />}
        </TrackerCard>
      )}

      {isEuipo && (
        <TrackerCard title="Calcul taxe EUIPO (RCD)" badge="tarife 2025, EUR">
          <table className="fee-table">
            <tbody>
              {r.haga ? (
                <FeeRow label={`Desemnare Haga (${taxeEuipoCalc.n} × ${taxeEuipo.haga_desemnare} €)`} value={taxeEuipoCalc.depunere} />
              ) : (
                <>
                  <FeeRow label="Depunere (primul design)" value={taxeEuipo.depunere_primul} />
                  {taxeEuipoCalc.n > 1 && <FeeRow label={`Depunere suplimentara (${taxeEuipoCalc.n - 1} × ${taxeEuipo.depunere_suplimentar} €)`} value={(taxeEuipoCalc.n - 1) * taxeEuipo.depunere_suplimentar} />}
                </>
              )}
              {taxeEuipoCalc.amanare > 0 && (
                <>
                  <FeeRow label={`Amanare publicare (primul: ${taxeEuipo.amanare_primul} €)`} value={taxeEuipo.amanare_primul} />
                  {taxeEuipoCalc.n > 1 && <FeeRow label={`Amanare supl. (${taxeEuipoCalc.n - 1} × ${taxeEuipo.amanare_suplimentar} €)`} value={(taxeEuipoCalc.n - 1) * taxeEuipo.amanare_suplimentar} />}
                </>
              )}
            </tbody>
            <tfoot>
              <tr className="fee-total">
                <td>Total taxe EUIPO</td>
                <td>{fmtEur(taxeEuipoCalc.total)}</td>
              </tr>
            </tfoot>
          </table>
          <button type="button" className="btn-link mt8" onClick={() => setShowTarife(s => !s)}>
            {showTarife ? '▾' : '▸'} Tarife oficiale (editabile)
          </button>
          {showTarife && <TaxeEditorEuipo taxe={taxeEuipo} setTaxe={setTaxeEuipo} />}
        </TrackerCard>
      )}

      {/* Depunere + monitorizare WIPO Hague */}
      {isWipo && (
        <TrackerCard title="Depunere si monitorizare internationala">
          <label className="check-label">
            <input type="checkbox" checked={r.depusa} onChange={e => set({ depusa: e.target.checked })} />
            <strong>Cererea internationala a fost depusa</strong>
          </label>
          {r.depusa && (
            <div className="dep-section">
              <div className="f-row">
                <div className="field">
                  <label>Nr. inregistrare internationala</label>
                  <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })} className="mono" placeholder="ex: DM/123456" />
                </div>
                <div className="field">
                  <label>Data depunerii</label>
                  <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
                </div>
              </div>

              <div className="tr-divider">Publicare &amp; inregistrare</div>

              <div className="f-row">
                <div className="field">
                  <label>Data publicarii in Buletinul WIPO</label>
                  <DateField value={r.dataPublicareWIPO} onChange={v => set({ dataPublicareWIPO: v })} />
                </div>
                <div className="field">
                  <label>Data inregistrarii internationale</label>
                  <DateField value={r.dataInregistrare} onChange={v => set({ dataInregistrare: v })} />
                </div>
                <div className="field">
                  <label>Data expirarii</label>
                  <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
                </div>
              </div>

              {status.expirare && (
                <div className="f-row">
                  <DeadlineChip label="Expirare protectie initiala (5 ani)" date={status.expirare} act />
                </div>
              )}
              <p className="f-hint">Reinnoibil din 5 in 5 ani, pana la maxim 25 de ani.</p>

              {reinnoiriWipo.length > 0 && (
                <>
                  <div className="tr-divider">Scadentar reinnoiri WIPO Hague</div>
                  <table className="fee-table">
                    <thead>
                      <tr>
                        <th>Reinnoire</th>
                        <th>Ani</th>
                        <th>Expira</th>
                        <th>Fereastra start</th>
                        <th>Gratie</th>
                        <th>Platit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reinnoiriWipo.map(per => (
                        <tr key={per.nr} className={per.platit ? 'fee-row-done' : ''}>
                          <td>Reinnoire {per.nr}</td>
                          <td className="mono">{per.ani}</td>
                          <td className="mono">{fmtRO(per.expira)}</td>
                          <td className="mono">{fmtRO(per.fereastraStart)}</td>
                          <td className="mono">{fmtRO(per.gratie)}</td>
                          <td>
                            <input type="checkbox" checked={per.platit} onChange={() => toggleReinnoireWipo(per.nr)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="tr-divider">Finalizare</div>

              <label className="check-label">
                <input type="checkbox" checked={r.certificatPrimit} onChange={e => set({ certificatPrimit: e.target.checked })} />
                Certificatul international a fost primit
              </label>
              <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
                <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
                Certificatul a fost transmis catre client
              </label>
            </div>
          )}
        </TrackerCard>
      )}

      {/* Depunere + monitorizare OSIM / EUIPO */}
      {!isWipo && (
        <TrackerCard title="Depunere si monitorizare">
          <label className="check-label">
            <input type="checkbox" checked={r.depusa} onChange={e => set({ depusa: e.target.checked })} />
            <strong>Cererea a fost depusa</strong>
          </label>

          {r.depusa && (
            <div className="dep-section">
              <div className="f-row">
                <div className="field">
                  <label>{isOsim ? 'Nr. depozit / inregistrare OSIM' : 'Nr. inregistrare RCD'}</label>
                  <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })} className="mono" />
                </div>
                <div className="field">
                  <label>Data depunerii</label>
                  <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
                </div>
              </div>

              <div className="tr-divider">Examinare &amp; publicare</div>

              <div className="field">
                <label>Data examinarii formale</label>
                <DateField value={r.dataExaminare} onChange={v => set({ dataExaminare: v })} />
              </div>
              {isOsim && parseISO(r.dataDepunere) && !r.dataExaminare && (
                <div className="f-row">
                  <DeadlineChip label="Termen examinare formala (5 zile)" date={addDays(parseISO(r.dataDepunere), 5)} act />
                </div>
              )}
              <div className="field">
                <label>Data publicarii {isOsim ? '(BOPI)' : ''}</label>
                <DateField value={r.dataPublicare} onChange={v => set({ dataPublicare: v })} disabled={r.amanarePublicare} />
              </div>

              {(isOsim || isEuipo) && (
                <div className="field">
                  <label>Data inregistrarii</label>
                  <DateField value={r.dataInregistrare} onChange={v => set({ dataInregistrare: v })} />
                </div>
              )}
              <div className="field">
                <label>Data expirarii</label>
                <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
              </div>

              {status.expirare && (
                <div className="f-row">
                  <DeadlineChip label="Expirare protectie (5 ani)" date={status.expirare} act />
                </div>
              )}
              <p className="f-hint">Reinnoibil din 5 in 5 ani, pana la maxim {isOsim ? '25' : '25'} de ani.</p>

              {isOsim && perioadeOsim.length > 0 && (
                <>
                  <div className="tr-divider">Scadentar mentineri &amp; reinnoiri OSIM</div>
                  <table className="fee-table">
                    <thead>
                      <tr>
                        <th>Perioada</th>
                        <th>Ani</th>
                        <th>Scadenta</th>
                        <th>Gratie</th>
                        <th>Suma (LEI)</th>
                        <th>Platit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perioadeOsim.map(per => (
                        <tr key={per.nr} className={per.platit ? 'fee-row-done' : ''}>
                          <td>{per.label}</td>
                          <td className="mono">{per.start + 1}–{per.start + 5}</td>
                          <td className="mono">{fmtRO(per.scadenta)}</td>
                          <td className="mono">{fmtRO(per.gratie)}</td>
                          <td>{fmtLei(per.suma + per.certReinnoire)}</td>
                          <td>
                            <input type="checkbox" checked={per.platit} onChange={() => togglePerioadaOsim(per.nr)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {isEuipo && reinnoiriEuipo.length > 0 && (
                <>
                  <div className="tr-divider">Scadentar reinnoiri EUIPO</div>
                  <table className="fee-table">
                    <thead>
                      <tr>
                        <th>Reinnoire</th>
                        <th>Expira</th>
                        <th>Fereastra start</th>
                        <th>Gratie</th>
                        <th>Suma (EUR)</th>
                        <th>Platit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reinnoiriEuipo.map(per => (
                        <tr key={per.nr} className={per.platit ? 'fee-row-done' : ''}>
                          <td>{per.label}</td>
                          <td className="mono">{fmtRO(per.expira)}</td>
                          <td className="mono">{fmtRO(per.fereastraStart)}</td>
                          <td className="mono">{fmtRO(per.gratie)}</td>
                          <td>{fmtEur(per.suma)}</td>
                          <td>
                            <input type="checkbox" checked={per.platit} onChange={() => toggleReinnoireEuipo(per.nr)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="tr-divider">Finalizare</div>

              <label className="check-label">
                <input type="checkbox" checked={r.certificatPrimit} onChange={e => set({ certificatPrimit: e.target.checked })} />
                Certificatul de inregistrare a fost primit
              </label>
              <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
                <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
                Certificatul a fost transmis catre client
              </label>
            </div>
          )}
        </TrackerCard>
      )}

            {/* ── Reinnoire ── */}
      <ReinnoireCard r={r} set={set} TrackerCard={TrackerCard} />

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })} rows={3} placeholder="Note de dosar…" />
      </TrackerCard>

      {/* Documente inregistrare */}
      <TrackerCard title="Documente inregistrare">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          tipOptions={['Duplicat Cerere inregistrare', 'Copie Certificat Inregistrare']}
          keyPrefix="inreg"
        />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>
                {done ? '✓' : i + 1}
              </span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div
        className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <span className="tr-card-title">{title}</span>
        <div className="tr-card-head-right">
          {badge && <span className="tr-badge">{badge}</span>}
          {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
        </div>
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options, disabled }) {
  return (
    <div className={`seg-ctrl${disabled ? ' seg-disabled' : ''}`}>
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled}
          className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => !disabled && onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, strong, muted, lei }) {
  return (
    <tr className={strong ? 'fee-strong' : muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{lei ? fmtLei(value) : fmtEur(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}

function TarifeEditorOsim({ tarife, setTarife }) {
  const fields = [
    ['depunere_primul',       'Depunere (primul design, LEI)'],
    ['depunere_suplimentar',  'Depunere (fiecare design supl., LEI)'],
    ['publicare_figura_an',   'Publicare figura alb-negru (LEI)'],
    ['publicare_figura_color','Publicare figura color (LEI)'],
    ['publicare_text',        'Publicare bloc text 30 cuv. (LEI)'],
    ['amanare_publicare',     'Taxa amanare publicare (LEI)'],
    ['examinare_primul',      'Examinare (primul design, LEI)'],
    ['examinare_suplimentar', 'Examinare (supl., LEI)'],
    ['certificat_1_20',       'Certificat lot 1–20 (LEI)'],
    ['certificat_21_50',      'Certificat lot 21–50 (LEI)'],
    ['certificat_51_100',     'Certificat lot 51–100 (LEI)'],
    ['mentinere_1_20',        'Mentinere per. I lot 1–20 (LEI)'],
    ['mentinere_21_50',       'Mentinere per. I lot 21–50 (LEI)'],
    ['mentinere_51_100',      'Mentinere per. I lot 51–100 (LEI)'],
    ['reinnoire_1_20',        'Reinnoire lot 1–20 (LEI)'],
    ['reinnoire_21_50',       'Reinnoire lot 21–50 (LEI)'],
    ['reinnoire_51_100',      'Reinnoire lot 51–100 (LEI)'],
    ['cert_reinnoire_1_20',   'Cert. reinnoire lot 1–20 (LEI)'],
    ['haga_primul',           'Desemnare OSIM prin Haga — primul (LEI)'],
    ['haga_urmatoarele',      'Desemnare OSIM prin Haga — fiecare urm. (LEI)'],
  ];
  return (
    <div className="tarife-editor">
      {fields.map(([k, lbl]) => (
        <label key={k} className="tarife-field">
          {lbl}
          <input type="number" value={tarife[k] ?? 0}
            onChange={e => setTarife({ ...tarife, [k]: Number(e.target.value) || 0 })} />
        </label>
      ))}
    </div>
  );
}

function TaxeEditorEuipo({ taxe, setTaxe }) {
  const fields = [
    ['depunere_primul',      'Depunere (primul design, EUR)'],
    ['depunere_suplimentar', 'Depunere (fiecare design supl., EUR)'],
    ['amanare_primul',       'Amanare publicare (primul design, EUR)'],
    ['amanare_suplimentar',  'Amanare publicare (design supl., EUR)'],
    ['reinnoire_1',          'Reinnoire (1 design, EUR)'],
    ['reinnoire_2',          'Reinnoire (2 design-uri, EUR)'],
    ['reinnoire_3',          'Reinnoire (3 design-uri, EUR)'],
    ['reinnoire_4',          'Reinnoire (4+ design-uri, EUR)'],
    ['haga_desemnare',       'Desemnare EUIPO prin Haga (EUR)'],
    ['haga_reinnoire',       'Reinnoire EUIPO prin Haga (EUR)'],
  ];
  return (
    <div className="tarife-editor">
      {fields.map(([k, lbl]) => (
        <label key={k} className="tarife-field">
          {lbl}
          <input type="number" value={taxe[k] ?? 0}
            onChange={e => setTaxe({ ...taxe, [k]: Number(e.target.value) || 0 })} />
        </label>
      ))}
    </div>
  );
}
